const ns: any = {}

import { createApp } from 'vue'

enum Loader {
    lazy = 'lazy',
    static = 'static'
}

type Attributes = Record<string, string>
type CallbackFunctionProcess = (entry: EntrySelectorComponent) => void
type CallbackFunctionElement = (entry: EntrySelectorComponent, element: Element, attributes: Attributes) => void

interface EntrySelectorComponent {
    selector: string
    component: any
    loader: Loader,
    preProcessCallback: CallbackFunctionProcess | undefined
    postProcessCallback: CallbackFunctionProcess | undefined
    preElementCallback: CallbackFunctionElement | undefined
    postElementCallback: CallbackFunctionElement | undefined
}

class Vue3Initializer {

    entries: EntrySelectorComponent[]

    constructor() {

        this.entries = []

        addEventListener('DOMContentLoaded', () => { this.process() })

    }

    addEntrySelectorComponent(
        selector: string,
        component: any,
        loader: string,
        preProcessCallback: CallbackFunctionProcess | undefined = undefined,
        postProcessCallback: CallbackFunctionProcess | undefined = undefined,
        preElementCallback: CallbackFunctionElement | undefined = undefined,
        postElementCallback: CallbackFunctionElement | undefined = undefined
    ) {

        this.entries.push({
            selector: selector,
            component: component,
            loader: loader,
            preProcessCallback,
            postProcessCallback,
            preElementCallback,
            postElementCallback
        } as EntrySelectorComponent)

    }

    bootstrapComponent(entry: EntrySelectorComponent, element: Element, component: any): void {

        if (element.getAttribute('data-dynamic-component') !== null || '__vue_app__' in element) {
            return
        }

        const attributes: Attributes = {}
        for (const attribute of element.attributes) {
            attributes[attribute.name] = attribute.value
        }

        entry.preElementCallback ? entry.preElementCallback(entry, element, attributes) : null

        const fragment = document.createDocumentFragment() as any

        attributes['data-dynamic-component'] = 'true'

        createApp(component, attributes).mount(fragment)

        element.replaceWith(fragment)

        entry.postElementCallback ? entry.postElementCallback(entry, element, attributes) : null

    }

    processElements(entry: EntrySelectorComponent, elements: NodeListOf<Element>): void {

        for (const element of elements) {

            if (entry.loader === Loader.lazy) {
                Promise
                    .resolve(entry.component)
                    .then((module) => {
                        this.bootstrapComponent(entry, element, module.default)
                    })
            }

            if (entry.loader === Loader.static) {
                this.bootstrapComponent(entry, element, entry.component)
            }

        }

    }

    process(): void {

        for (const entry of this.entries) {

            const elements = document.querySelectorAll(entry.selector)

            if (elements.length === 0) continue

            entry.preProcessCallback ? entry.preProcessCallback(entry) : null

            this.processElements(entry, elements)

            entry.postProcessCallback ? entry.postProcessCallback(entry) : null

        }

    }

    print(): void {

        for (const entry of this.entries) {
            console.log({ entry })
        }

    }

    reinitialize(): void {

        this.process()

    }

    deinitialize(parent: HTMLElement | undefined = undefined): void {

        const components = (parent || document).querySelectorAll('[data-dynamic-component="true"]')

        for (const component of components) {
            this.destroyComponent(component)
        }

    }

    destroyComponent(element: any): void {

        if (!('__vue_app__' in element)) {
            return;
        }

        try {
            element.__vue_app__.unmount()
        } catch (e) {
            console.warn('Could not unmount dynamic component on element!')
        }

    }

}

ns.vue3initializer = new Vue3Initializer()

export default ns.vue3initializer

export {
    Vue3Initializer,
    Loader,
    EntrySelectorComponent,
    Attributes,
    CallbackFunctionProcess,
    CallbackFunctionElement
}
